/*
 * FileSaver.js
 * A saveAs() FileSaver implementation.
 *
 * By Eli Grey, http://eligrey.com
 *
 * License : https://github.com/eligrey/FileSaver.js/blob/master/LICENSE.md (MIT)
 * source  : http://purl.eligrey.com/github/FileSaver.js
 */

// Extend Navigator interface for legacy msSaveOrOpenBlob
interface NavigatorExtended extends Navigator {
  msSaveOrOpenBlob?: (blob: Blob, fileName: string) => boolean;
}

// Extend Blob interface to include name property (for File objects)
interface BlobWithName extends Blob {
  name?: string;
}

// Extend Window interface for webkit and global properties
interface WindowExtended extends Window {
  webkitURL?: typeof URL;
  safari?: any;
  HTMLElement?: typeof HTMLElement;
}

interface GlobalContext {
  navigator?: NavigatorExtended;
  URL?: typeof URL;
  webkitURL?: typeof URL;
  HTMLElement?: typeof HTMLElement;
  safari?: any;
  saveAs?: SaveAsFunction;
}

interface SaveOptions {
  autoBom?: boolean;
  control?: string;
}

interface Header {
  name: string;
  value: string;
}

type SaveAsFunction = (
  blob: BlobWithName | string,
  name?: string,
  opts?: SaveOptions,
  popup?: Window | null
) => void;

// The one and only way of getting global scope in all environments
// https://stackoverflow.com/q/3277182/1008999
let control: HTMLElement | null;
const _global: WindowExtended & GlobalContext = (typeof window === 'object' && window.window === window ?
  window : typeof self === 'object' && self.self === self ?
    self : typeof global === 'object' && (global as any).global === global ?
      global :
      this) as unknown as WindowExtended & GlobalContext;

function bom(blob: Blob, opts?: SaveOptions): Blob {
  if (typeof opts === 'undefined') {
    opts = {
      autoBom: false
    };
  } else if (typeof opts !== 'object') {
    console.warn('Deprecated: Expected third argument to be a object');
    opts = {
      autoBom: !opts
    };
  }

  // prepend BOM for UTF-8 XML and text/* types (including HTML)
  // note: your browser will automatically convert UTF-16 U+FEFF to EF BB BF
  if (opts.autoBom && /^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(blob.type)) {
    return new Blob([String.fromCharCode(0xFEFF), blob], {
      type: blob.type
    });
  }
  return blob;
}

function download(
  url: string,
  name: string,
  opts: SaveOptions,
  onloadstart?: () => ((this: XMLHttpRequest, ev: ProgressEvent) => any) | null,
  onprogress?: () => ((this: XMLHttpRequest, ev: ProgressEvent) => any) | null,
  onloadend?: () => ((this: XMLHttpRequest, ev: ProgressEvent) => any) | null,
  onerror?: () => ((this: XMLHttpRequest, ev: ProgressEvent) => any) | null,
  headers: Header[] = []
): void {
  control = opts.control ? document.querySelector(opts.control) : null;
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url);
  for (const header of headers) {
    xhr.setRequestHeader(header.name, header.value);
  }
  xhr.responseType = 'blob';

  if (onloadstart && typeof onloadstart === 'function') {
    xhr.onloadstart = onloadstart();
  } else {
    xhr.onloadstart = function () {
      if (control) {
        control.classList.add('downloading');
      }
    };
  }

  if (onprogress && typeof onprogress === 'function') {
    xhr.onprogress = onprogress();
  }

  if (onloadend && typeof onloadend === 'function') {
    xhr.onloadend = onloadend();
  } else {
    xhr.onloadend = function () {
      if (control) {
        control.classList.remove('downloading');
      }
    };
  }

  if (onerror && typeof onerror === 'function') {
    xhr.onerror = onerror();
  } else {
    xhr.onerror = function () {
      console.error('could not download file');
      if (control) {
        control.classList.remove('downloading');
      }
    };
  }

  xhr.onload = function () {
    saveAs(xhr.response, name, opts);
  };

  xhr.send();
}

function corsEnabled(url: string): boolean {
  const xhr = new XMLHttpRequest();
  // use sync to avoid popup blocker
  xhr.open('HEAD', url, false);
  try {
    xhr.send();
  } catch (e) { }
  return xhr.status >= 200 && xhr.status <= 299;
}

// `a.click()` doesn't work for all browsers (#465)
function click(node: HTMLAnchorElement): void {
  try {
    node.dispatchEvent(new MouseEvent('click'));
  } catch (e) {
    const evt = document.createEvent('MouseEvents');
    evt.initMouseEvent('click', true, true, window, 0, 0, 0, 80,
      20, false, false, false, false, 0, null);
    node.dispatchEvent(evt);
  }
}

// Detect WebView inside a native macOS app by ruling out all browsers
// We just need to check for 'Safari' because all other browsers (besides Firefox) include that too
// https://www.whatismybrowser.com/guides/the-latest-user-agent/macos
const isMacOSWebView = _global.navigator && /Macintosh/.test(navigator.userAgent) && /AppleWebKit/.test(navigator.userAgent) && !/Safari/.test(navigator.userAgent);

const saveAs: SaveAsFunction = _global.saveAs || (
  // probably in some web worker
  (typeof window !== 'object' || window !== _global) ?
    function saveAs(): void {
      /* noop */
    }

    // Use download attribute first if possible (#193 Lumia mobile) unless this is a macOS WebView
    :
    ('download' in HTMLAnchorElement.prototype && !isMacOSWebView) ?
      function saveAs(blob: BlobWithName | string, name?: string, opts?: SaveOptions): void {
        const URL = _global.URL || _global.webkitURL;
        const a = document.createElement('a');
        const fileName = name || (typeof blob !== 'string' ? blob.name : undefined) || 'download';

        a.download = fileName;
        a.rel = 'noopener'; // tabnabbing

        // TODO: detect chrome extensions & packaged apps
        // a.target = '_blank'

        if (typeof blob === 'string') {
          // Support regular links
          a.href = blob;
          if (a.origin !== location.origin) {
            if (corsEnabled(a.href)) {
              download(blob, fileName, opts || {});
            } else {
              a.target = '_blank';
              click(a);
            }
          } else {
            click(a);
          }
        } else {
          // Support blobs
          a.href = URL!.createObjectURL(blob);
          setTimeout(function () {
            URL!.revokeObjectURL(a.href);
          }, 4E4); // 40s
          setTimeout(function () {
            click(a);
          }, 0);
        }
      }

      // Use msSaveOrOpenBlob as a second approach
      :
      'msSaveOrOpenBlob' in navigator ?
        function saveAs(blob: BlobWithName | string, name?: string, opts?: SaveOptions): void {
          const fileName = name || (typeof blob !== 'string' ? blob.name : undefined) || 'download';

          if (typeof blob === 'string') {
            if (corsEnabled(blob)) {
              download(blob, fileName, opts || {});
            } else {
              const a = document.createElement('a');
              a.href = blob;
              a.target = '_blank';
              setTimeout(function () {
                click(a);
              });
            }
          } else {
            (navigator as NavigatorExtended).msSaveOrOpenBlob!(bom(blob, opts), fileName);
          }
        }

        // Fallback to using FileReader and a popup
        :
        function saveAs(blob: BlobWithName | string, name?: string, opts?: SaveOptions, popup?: Window | null): void {
          // Open a popup immediately do go around popup blocker
          // Mostly only available on user interaction and the fileReader is async so...
          popup = popup || open('', '_blank');
          if (popup) {
            popup.document.title =
              popup.document.body.innerText = 'downloading...';
          }

          if (typeof blob === 'string') {
            download(blob, name || 'download', opts || {});
            return;
          }

          const force = blob.type === 'application/octet-stream';
          const isSafari = /constructor/i.test(String(_global.HTMLElement)) || _global.safari;
          const isChromeIOS = /CriOS\/[\d]+/.test(navigator.userAgent);

          if ((isChromeIOS || (force && isSafari) || isMacOSWebView) && typeof FileReader !== 'undefined') {
            // Safari doesn't allow downloading of blob URLs
            const reader = new FileReader();
            reader.onloadend = function () {
              let url = reader.result as string;
              url = isChromeIOS ? url : url.replace(/^data:[^;]*;/, 'data:attachment/file;');
              if (popup) {
                popup.location.href = url;
              } else {
                location.href = url;
              }
              popup = null; // reverse-tabnabbing #460
            };
            reader.readAsDataURL(blob);
          } else {
            const URL = _global.URL || _global.webkitURL;
            const url = URL!.createObjectURL(blob);
            if (popup) {
              popup.location.href = url;
            } else {
              location.href = url;
            }
            popup = null; // reverse-tabnabbing #460
            setTimeout(function () {
              URL!.revokeObjectURL(url);
            }, 4E4); // 40s
          }
        }
);

(_global as any).saveAs = (saveAs as any).saveAs = saveAs;

export default saveAs;
export { saveAs, bom, download, corsEnabled, click };

